import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import {
  Robot, Brain, Lightning, Sparkle, ChartLineUp,
  CheckCircle, ArrowRight, Phone, Calendar, Shield,
  FileText, Clock, TrendUp, Target, Cpu
} from "@phosphor-icons/react";

const HospiceAIPage = () => {
  const aiFeatures = [
    {
      title: "AI Documentation Assistant",
      description: "Reduce documentation time by 40% with AI-powered note generation, clinical terminology suggestions, and compliance checking.",
      icon: FileText,
      benefits: ["Faster documentation", "Improved accuracy", "Compliance built-in"]
    },
    {
      title: "Predictive Analytics",
      description: "AI models that predict patient decline, optimize visit schedules, and identify patients at risk for hospitalization.",
      icon: ChartLineUp,
      benefits: ["Proactive care", "Better outcomes", "Resource optimization"]
    },
    {
      title: "Compliance Monitoring",
      description: "Real-time AI scanning of documentation for compliance gaps, missing elements, and audit risk indicators.",
      icon: Shield,
      benefits: ["Continuous monitoring", "Early warning alerts", "Audit prevention"]
    },
    {
      title: "Quality Intelligence",
      description: "AI-powered quality metrics tracking, trend analysis, and performance improvement recommendations.",
      icon: Target,
      benefits: ["Automated QAPI", "Actionable insights", "Benchmark comparisons"]
    }
  ];

  const useCases = [
    {
      title: "Clinical Decision Support",
      desc: "AI helps clinicians make better care decisions with evidence-based recommendations."
    },
    {
      title: "Staffing Optimization",
      desc: "Predict patient needs and optimize nurse schedules for better coverage and efficiency."
    },
    {
      title: "Revenue Cycle",
      desc: "AI identifies billing opportunities, documentation gaps, and denial risks before they occur."
    },
    {
      title: "Family Communication",
      desc: "AI-generated summaries help families understand patient status and care plans clearly."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>AI for Hospice Care | Hospice AI Solutions | Noble Hospice Solutions</title>
        <meta name="description" content="Transform your hospice with AI-powered solutions. AI documentation, predictive analytics, compliance monitoring, and quality intelligence for hospice agencies. See how AI can revolutionize your hospice operations." />
        <meta name="keywords" content="hospice AI, AI hospice care, artificial intelligence hospice, hospice technology, hospice automation, hospice predictive analytics, AI documentation hospice, hospice software AI" />
        <link rel="canonical" href="https://noblehospicesolutions.com/hospice-ai" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Hospice AI Solutions",
            "provider": {
              "@type": "Organization",
              "name": "Noble Hospice Solutions",
              "url": "https://noblehospicesolutions.com"
            },
            "description": "AI-powered solutions for hospice agencies including documentation automation, predictive analytics, and compliance monitoring.",
            "areaServed": "United States"
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-purple-900/50 to-slate-900" />
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500/30 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/20 text-purple-400 text-sm font-semibold mb-6">
              <Cpu weight="fill" className="w-4 h-4" />
              AI-Powered Hospice Solutions
            </span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-white font-bold mb-6">
              Hospice AI
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-cyan-400">
                The Future of Hospice Care
              </span>
            </h1>
            <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
              Harness the power of artificial intelligence to improve patient outcomes, 
              reduce documentation burden, and achieve operational excellence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/schedule-demo"
                className="btn-primary inline-flex items-center justify-center gap-2"
              >
                <Robot weight="bold" className="w-5 h-5" />
                See AI in Action
              </Link>
              <Link
                to="/client-portal"
                className="btn-secondary inline-flex items-center justify-center gap-2"
              >
                <Brain weight="bold" className="w-5 h-5" />
                Try AI Portal
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* AI Features */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl md:text-4xl text-slate-900 font-bold mb-4">
              AI Solutions for Modern Hospice
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Our AI platform is purpose-built for hospice care, addressing the unique challenges 
              of end-of-life care delivery.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {aiFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gradient-to-br from-slate-50 to-purple-50/50 rounded-2xl p-8 border border-slate-200 hover:border-purple-500/50 transition-all"
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-cyan-600 flex items-center justify-center mb-6">
                  <feature.icon weight="fill" className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-display text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-600 mb-4">{feature.description}</p>
                <div className="flex flex-wrap gap-2">
                  {feature.benefits.map((benefit, i) => (
                    <span key={i} className="px-3 py-1 bg-purple-100 text-purple-700 text-sm rounded-full font-medium">
                      {benefit}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-purple-900/50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl md:text-4xl text-white font-bold mb-4">
              AI Use Cases in Hospice
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              From clinical care to operations, AI is transforming every aspect of hospice delivery.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {useCases.map((useCase, index) => (
              <motion.div
                key={useCase.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20"
              >
                <Lightning weight="fill" className="w-8 h-8 text-purple-400 mb-4" />
                <h3 className="font-display text-lg font-bold text-white mb-2">{useCase.title}</h3>
                <p className="text-slate-300 text-sm">{useCase.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            {[
              { stat: "40%", label: "Documentation Time Saved" },
              { stat: "25%", label: "Improved Patient Outcomes" },
              { stat: "60%", label: "Faster Compliance Reviews" },
              { stat: "3x", label: "ROI on AI Investment" }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <div className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-cyan-600 mb-2">
                  {item.stat}
                </div>
                <div className="text-slate-600 font-medium">{item.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-cyan-600">
        <div className="container mx-auto px-6 text-center">
          <h2 className="font-display text-3xl md:text-4xl text-white font-bold mb-6">
            Ready to Bring AI to Your Hospice?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            See how AI can transform your operations. Schedule a demo of our AI-powered client portal today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/schedule-demo"
              className="bg-white text-purple-600 font-bold px-8 py-4 rounded-full hover:shadow-xl transition-all inline-flex items-center justify-center gap-2"
            >
              Schedule AI Demo
              <ArrowRight weight="bold" className="w-5 h-5" />
            </Link>
            <Link
              to="/client-portal"
              className="bg-white/20 text-white font-bold px-8 py-4 rounded-full hover:bg-white/30 transition-all"
            >
              Try Client Portal
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HospiceAIPage;
